import React, { useEffect, useRef, useState } from "react";
import {
  fetchDocumentCategoryUniqueNumber,
  uploadVaultDocument,
} from "./qmsService";
import {
  apiMessage,
  firstSuccessStoryRow,
  isApiSuccess,
  normalizeSuccessStoryList,
} from "./qmsApiUtils";
import {
  getDefaultVaultUploadTitle,
  parseVaultCategoryUniqueNumber,
} from "./qmsVaultUtils";
import QmsModalPortal from "./QmsModalPortal";
import QmsTagsInput from "./QmsTagsInput";

const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "png",
  "jpg",
  "jpeg",
];

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",").pop() : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

/** Letters, numbers, spaces, hyphen, underscore only. */
const DOCUMENT_TITLE_PATTERN = /^[A-Za-z0-9 _-]+$/;

function normalizeDocumentTitle(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function sanitizeDocumentTitleInput(value) {
  return String(value || "").replace(/[^A-Za-z0-9 _-]/g, "");
}

function getDocumentTitleError(documentTitle) {
  const title = normalizeDocumentTitle(documentTitle);
  if (!title) {
    return "Document title is required. Spaces alone are not allowed.";
  }
  if (!DOCUMENT_TITLE_PATTERN.test(title)) {
    return "Document title can only contain letters, numbers, spaces, hyphens (-), and underscores (_).";
  }
  if (!/[A-Za-z]/.test(title)) {
    return "Document title must include at least one letter. Numbers alone are not allowed.";
  }
  // Ignore spaces / separators when checking "all same character" (e.g. aaa, 111, a a a).
  const significant = title.replace(/[ _-]/g, "");
  if (significant.length >= 2 && /^(.)\1+$/i.test(significant)) {
    return "Document title cannot be the same character repeated.";
  }
  return "";
}

function buildValidation(file, documentTypeId, documentCategoryId, uniqueNumber, documentTitle) {
  const fieldErrors = {};

  if (!file) {
    fieldErrors.file = "Please choose a file to upload.";
  } else {
    const extension = file.name.includes(".")
      ? file.name.split(".").pop().toLowerCase()
      : "";
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      fieldErrors.file = `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}.`;
    }
  }

  if (!documentTypeId) fieldErrors.documentTypeId = "Document type is required.";
  if (!documentCategoryId) {
    fieldErrors.documentCategoryId = "Document category is required.";
  }
  if (!parseVaultCategoryUniqueNumber(uniqueNumber)) {
    fieldErrors.uniqueNumber = "Unique number is required.";
  }

  const titleError = getDocumentTitleError(documentTitle);
  if (titleError) fieldErrors.documentTitle = titleError;

  const missingLabels = [];
  if (fieldErrors.file) missingLabels.push("file");
  if (fieldErrors.documentTypeId) missingLabels.push("document type");
  if (fieldErrors.documentCategoryId) missingLabels.push("document category");
  if (fieldErrors.uniqueNumber) missingLabels.push("unique number");
  if (fieldErrors.documentTitle) missingLabels.push("document title");

  let summary = "";
  if (Object.keys(fieldErrors).length === 1 && fieldErrors.documentTitle) {
    summary = fieldErrors.documentTitle;
  } else if (missingLabels.length === 1 && fieldErrors.file?.startsWith("Unsupported")) {
    summary = fieldErrors.file;
  } else if (missingLabels.length === 1) {
    summary = `Please complete the required field: ${missingLabels[0]}.`;
  } else if (missingLabels.length > 1) {
    summary = `Please complete the required fields: ${missingLabels.join(", ")}.`;
  }

  return { fieldErrors, summary };
}

function QmsVaultUploadModal({
  open,
  onClose,
  userData,
  documentTypes = [],
  documentCategories = [],
  saving = false,
  onSavingChange,
  onUploaded,
  onNotify,
}) {
  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);
  const [file, setFile] = useState(null);
  const [documentTypeId, setDocumentTypeId] = useState("");
  const [documentCategoryId, setDocumentCategoryId] = useState("");
  const [uniqueNumber, setUniqueNumber] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [metaTag, setMetaTag] = useState("");
  const [uniqueLoading, setUniqueLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setDocumentTypeId("");
    setDocumentCategoryId("");
    setUniqueNumber("");
    setDocumentTitle("");
    setMetaTag("");
    setUniqueLoading(false);
    setFormError("");
    setFieldErrors({});
    setDragActive(false);
  }, [open]);

  useEffect(() => {
    if (!open || !documentCategoryId || !userData?.StaffId) {
      if (!documentCategoryId) setUniqueNumber("");
      return undefined;
    }

    let cancelled = false;
    setUniqueLoading(true);
    fetchDocumentCategoryUniqueNumber(userData, documentCategoryId)
      .then((response) => {
        if (cancelled) return;
        const row = firstSuccessStoryRow(response);
        const nextValue =
          row?.DocumentCategoryUniqueNumber ??
          normalizeSuccessStoryList(response)[0]?.DocumentCategoryUniqueNumber ??
          "";
        setUniqueNumber(String(nextValue ?? ""));
      })
      .catch(() => {
        if (!cancelled) setUniqueNumber("");
      })
      .finally(() => {
        if (!cancelled) setUniqueLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentCategoryId, open, userData]);

  if (!open) return null;

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const applySelectedFile = (nextFile) => {
    if (!nextFile) return;
    setFile(nextFile);
    clearFieldError("file");
    setFormError("");
    if (!normalizeDocumentTitle(documentTitle)) {
      setDocumentTitle(
        sanitizeDocumentTitleInput(getDefaultVaultUploadTitle(nextFile.name)),
      );
      clearFieldError("documentTitle");
    }
  };

  const handleFileChange = (event) => {
    applySelectedFile(event.target.files?.[0] || null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!saving) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (saving) return;
    applySelectedFile(event.dataTransfer.files?.[0] || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { fieldErrors: nextFieldErrors, summary } = buildValidation(
      file,
      documentTypeId,
      documentCategoryId,
      uniqueNumber,
      documentTitle,
    );

    if (summary) {
      setFieldErrors(nextFieldErrors);
      setFormError(summary);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      return;
    }

    onSavingChange?.(true);
    setFormError("");
    setFieldErrors({});

    try {
      const extension = file.name.includes(".")
        ? file.name.split(".").pop().toLowerCase()
        : "";
      const documentBase64 = await readFileAsBase64(file);
      const response = await uploadVaultDocument(userData, {
        ActionType: "UploadDocumentToMyVault",
        DocumentExtension: extension,
        DocumentSize: Math.round(file.size / 1024),
        DocumentBase64: documentBase64,
        DocumentTitle: normalizeDocumentTitle(documentTitle),
        DocumentType: documentTypeId,
        DocumentCategory: documentCategoryId,
        DocumentCategoryUniqueNumber: parseVaultCategoryUniqueNumber(uniqueNumber),
        MetaHashTag: metaTag.trim(),
      });

      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Upload failed.");
      }

      const result = firstSuccessStoryRow(response);
      onNotify?.({
        title: "Success",
        message: apiMessage(response) || "Document uploaded successfully.",
        type: "success",
      });
      onUploaded?.(result);
      onClose?.();
    } catch (error) {
      const message = error?.message || "Could not upload document.";
      setFormError(message);
      setFieldErrors({});
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      onNotify?.({
        title: "Upload failed",
        message,
        type: "danger",
      });
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <QmsModalPortal>
      <div
        className="qms-vault-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Upload document"
        onClick={() => {
          if (!saving) onClose?.();
        }}
      >
        <div
          className="qms-vault-upload-modal__dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="qms-vault-upload-modal__content">
            <div className="qms-vault-upload-modal__header">
              <div className="qms-vault-upload-modal__header-main">
                <div className="qms-vault-upload-modal__icon" aria-hidden="true">
                  <i className="fas fa-cloud-upload-alt" />
                </div>
                <div className="qms-vault-upload-modal__title-wrap">
                  <h5 className="qms-vault-upload-modal__title">Upload document</h5>
                  <p className="qms-vault-upload-modal__subtitle">
                    Add a new document to My Vault. Type, category, and unique number
                    are used to build the document ID.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={saving}
              />
            </div>

            <form className="qms-vault-upload-modal__form" onSubmit={handleSubmit}>
              <div className="qms-vault-upload-modal__body" ref={bodyRef}>
                {formError ? (
                  <div
                    className="qms-vault-upload-modal__alert"
                    role="alert"
                  >
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                ) : null}

                <section className="qms-vault-upload-modal__section">
                  <div className="qms-vault-upload-modal__section-head">
                    <span className="qms-vault-upload-modal__section-step">1</span>
                    <div>
                      <h6 className="qms-vault-upload-modal__section-title">
                        Choose file
                      </h6>
                      <p className="qms-vault-upload-modal__section-desc">
                        PDF, Office documents, images, or plain text.
                      </p>
                    </div>
                  </div>

                  <div
                    className={`qms-vault-upload-modal__dropzone${
                      dragActive ? " is-dragover" : ""
                    }${file ? " has-file" : ""}${
                      fieldErrors.file ? " is-invalid" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      id="qms-vault-upload-file"
                      type="file"
                      className="qms-vault-upload-modal__file-input"
                      accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                      onChange={handleFileChange}
                      disabled={saving}
                    />
                    {file ? (
                      <div className="qms-vault-upload-modal__file-card">
                        <div
                          className="qms-vault-upload-modal__file-icon"
                          aria-hidden="true"
                        >
                          <i className="fas fa-file-alt" />
                        </div>
                        <div className="qms-vault-upload-modal__file-meta">
                          <strong>{file.name}</strong>
                          <span>{formatFileSize(file.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="qms-vault-upload-modal__file-change"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={saving}
                        >
                          Change file
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="qms-vault-upload-modal__dropzone-trigger"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={saving}
                      >
                        <span
                          className="qms-vault-upload-modal__dropzone-icon"
                          aria-hidden="true"
                        >
                          <i className="fas fa-arrow-up" />
                        </span>
                        <strong>Drop your file here, or browse</strong>
                        <span>
                          Max recommended size depends on your organization policy
                        </span>
                      </button>
                    )}
                  </div>
                  {fieldErrors.file ? (
                    <p className="qms-vault-upload-modal__field-error">
                      {fieldErrors.file}
                    </p>
                  ) : null}
                </section>

                <section className="qms-vault-upload-modal__section">
                  <div className="qms-vault-upload-modal__section-head">
                    <span className="qms-vault-upload-modal__section-step">2</span>
                    <div>
                      <h6 className="qms-vault-upload-modal__section-title">
                        Document details
                      </h6>
                      <p className="qms-vault-upload-modal__section-desc">
                        Required metadata used for identification and search.
                      </p>
                    </div>
                  </div>

                  <div className="qms-vault-upload-modal__grid">
                    <div className="qms-vault-upload-modal__field">
                      <label
                        className="qms-vault-upload-modal__label"
                        htmlFor="qms-vault-upload-type"
                      >
                        Document type <span className="is-required">*</span>
                      </label>
                      <select
                        id="qms-vault-upload-type"
                        className={`qms-vault-upload-modal__control${
                          fieldErrors.documentTypeId ? " is-invalid" : ""
                        }`}
                        value={documentTypeId}
                        onChange={(event) => {
                          setDocumentTypeId(event.target.value);
                          clearFieldError("documentTypeId");
                          setFormError("");
                        }}
                        disabled={saving}
                        aria-invalid={Boolean(fieldErrors.documentTypeId)}
                      >
                        <option value="">Select type</option>
                        {documentTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.documentTypeId ? (
                        <p className="qms-vault-upload-modal__field-error">
                          {fieldErrors.documentTypeId}
                        </p>
                      ) : null}
                    </div>

                    <div className="qms-vault-upload-modal__field">
                      <label
                        className="qms-vault-upload-modal__label"
                        htmlFor="qms-vault-upload-category"
                      >
                        Document category <span className="is-required">*</span>
                      </label>
                      <select
                        id="qms-vault-upload-category"
                        className={`qms-vault-upload-modal__control${
                          fieldErrors.documentCategoryId ? " is-invalid" : ""
                        }`}
                        value={documentCategoryId}
                        onChange={(event) => {
                          setDocumentCategoryId(event.target.value);
                          clearFieldError("documentCategoryId");
                          setFormError("");
                        }}
                        disabled={saving}
                        aria-invalid={Boolean(fieldErrors.documentCategoryId)}
                      >
                        <option value="">Select category</option>
                        {documentCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.documentCategoryId ? (
                        <p className="qms-vault-upload-modal__field-error">
                          {fieldErrors.documentCategoryId}
                        </p>
                      ) : null}
                    </div>

                    <div className="qms-vault-upload-modal__field">
                      <label
                        className="qms-vault-upload-modal__label"
                        htmlFor="qms-vault-upload-unique"
                      >
                        Unique number <span className="is-required">*</span>
                      </label>
                      <input
                        id="qms-vault-upload-unique"
                        type="text"
                        className={`qms-vault-upload-modal__control${
                          fieldErrors.uniqueNumber ? " is-invalid" : ""
                        }`}
                        maxLength={5}
                        value={uniqueNumber}
                        onChange={(event) => {
                          setUniqueNumber(event.target.value);
                          clearFieldError("uniqueNumber");
                          setFormError("");
                        }}
                        disabled={saving || uniqueLoading}
                        placeholder={uniqueLoading ? "Loading…" : "Unique number"}
                        aria-invalid={Boolean(fieldErrors.uniqueNumber)}
                      />
                      {fieldErrors.uniqueNumber ? (
                        <p className="qms-vault-upload-modal__field-error">
                          {fieldErrors.uniqueNumber}
                        </p>
                      ) : null}
                    </div>

                    <div className="qms-vault-upload-modal__field">
                      <label
                        className="qms-vault-upload-modal__label"
                        htmlFor="qms-vault-upload-title"
                      >
                        Document title <span className="is-required">*</span>
                      </label>
                      <input
                        id="qms-vault-upload-title"
                        type="text"
                        className={`qms-vault-upload-modal__control${
                          fieldErrors.documentTitle ? " is-invalid" : ""
                        }`}
                        value={documentTitle}
                        onChange={(event) => {
                          setDocumentTitle(
                            sanitizeDocumentTitleInput(event.target.value),
                          );
                          clearFieldError("documentTitle");
                          setFormError("");
                        }}
                        disabled={saving}
                        placeholder="Document title"
                        aria-invalid={Boolean(fieldErrors.documentTitle)}
                        title="Letters, numbers, spaces, hyphens, and underscores only"
                      />
                      {fieldErrors.documentTitle ? (
                        <p className="qms-vault-upload-modal__field-error">
                          {fieldErrors.documentTitle}
                        </p>
                      ) : (
                        <p className="qms-vault-upload-modal__field-hint">
                          Must include letters. Numbers alone or repeated characters
                          (e.g. aaaa, 1111) are not allowed.
                        </p>
                      )}
                    </div>

                    <div className="qms-vault-upload-modal__field qms-vault-upload-modal__field--full">
                      <QmsTagsInput
                        id="qms-vault-upload-meta"
                        label="Document meta tags"
                        value={metaTag}
                        onChange={setMetaTag}
                        disabled={saving}
                        placeholder="Add tags…"
                        hint="Press Enter, comma, or space to add a tag."
                      />
                    </div>
                  </div>
                </section>
              </div>

              <div className="qms-vault-upload-modal__footer">
                <button
                  type="button"
                  className="qms-vault-upload-modal__btn qms-vault-upload-modal__btn--ghost"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="qms-vault-upload-modal__btn qms-vault-upload-modal__btn--primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload" aria-hidden="true" />
                      Upload document
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </QmsModalPortal>
  );
}

export default QmsVaultUploadModal;
